import {
  Animated,
  ImageBackground,
  Text,
  View,
  type ImageSourcePropType,
} from "react-native";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { X } from "lucide-react-native";
import { Circle, Svg } from "react-native-svg";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { IconButton } from "../primitives/IconButton";
import { colors, radii, shadows, spacing, typography } from "../theme/tokens";

export type ToastOptions = {
  message: string;
  variant?: "error" | "info";
  duration?: number;
};

type ToastState = ToastOptions & { id: number };

type ToastContextValue = {
  showToast: (options: ToastOptions) => void;
  dismissToast: () => void;
};

const ToastContext = createContext<ToastContextValue>({
  showToast: () => {},
  dismissToast: () => {},
});

const TOAST_DURATION = 6000;
const CLOSE_RING_RADIUS = 9;
const CLOSE_RING_CIRCUMFERENCE = 2 * Math.PI * CLOSE_RING_RADIUS;
const AnimatedCircle = Animated.createAnimatedComponent(Circle);

function ToastViewport({
  toast,
  onDismiss,
  backgroundSource,
}: {
  toast: ToastState | null;
  onDismiss: () => void;
  backgroundSource: ImageSourcePropType;
}) {
  const insets = useSafeAreaInsets();
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    progress.stopAnimation();
    if (!toast) {
      progress.setValue(0);
      return;
    }

    progress.setValue(1);
    const animation = Animated.timing(progress, {
      toValue: 0,
      duration: toast.duration ?? TOAST_DURATION,
      useNativeDriver: false,
    });
    animation.start();

    return () => animation.stop();
  }, [progress, toast]);

  if (!toast) return null;

  return (
    <View
      pointerEvents="box-none"
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        left: 0,
        zIndex: 1000,
        elevation: 1000,
        paddingTop: insets.top + spacing.sm,
        paddingHorizontal: spacing.lg,
        alignItems: "center",
      }}
    >
      <ImageBackground
        source={backgroundSource}
        resizeMode="cover"
        imageStyle={{ borderRadius: radii.lg }}
        style={{
          maxWidth: 520,
          borderRadius: radii.lg,
          overflow: "hidden",
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.16)",
          ...shadows.lg,
        }}
      >
        <View
          pointerEvents="none"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(21, 10, 7, 0.1)",
          }}
        />
        <View style={{ position: "absolute", top: 8, right: 8 }}>
          <IconButton
            accessibilityLabel="Dismiss notification"
            onPress={onDismiss}
            size="sm"
            surface="soft"
            backgroundColor="rgba(255,255,255,0.1)"
            pressedBackgroundColor="rgba(255,255,255,0.18)"
          >
            <View
              style={{
                width: 22,
                height: 22,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Svg
                pointerEvents="none"
                width={22}
                height={22}
                viewBox="0 0 22 22"
                style={{ position: "absolute" }}
              >
                <Circle
                  cx={11}
                  cy={11}
                  r={CLOSE_RING_RADIUS}
                  fill="none"
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth={1.5}
                />
                <AnimatedCircle
                  cx={11}
                  cy={11}
                  r={CLOSE_RING_RADIUS}
                  fill="none"
                  stroke="rgba(255,247,239,0.92)"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeDasharray={[
                    CLOSE_RING_CIRCUMFERENCE,
                    CLOSE_RING_CIRCUMFERENCE,
                  ]}
                  strokeDashoffset={progress.interpolate({
                    inputRange: [0, 1],
                    outputRange: [CLOSE_RING_CIRCUMFERENCE, 0],
                  })}
                />
              </Svg>
              <X size={14} color="rgba(255,247,239,0.82)" strokeWidth={1.8} />
            </View>
          </IconButton>
        </View>
        <View
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
          style={{
            gap: spacing.sm,
            padding: spacing.md,
            paddingRight: spacing.md + 30,
          }}
        >
          <Text
            style={{
              fontFamily: typography.families.sans,
              fontSize: typography.sizes.base,
              lineHeight: typography.sizes.base * 1.45,
              color: colors.surface,
            }}
          >
            {toast.message}
          </Text>
        </View>
      </ImageBackground>
    </View>
  );
}

export function ToastProvider({
  children,
  backgroundSource,
}: {
  children: ReactNode;
  backgroundSource: ImageSourcePropType;
}) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const nextIdRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismissToast = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    setToast(null);
  }, []);

  const showToast = useCallback((options: ToastOptions) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const id = nextIdRef.current + 1;
    nextIdRef.current = id;
    setToast({ ...options, id });

    const duration = options.duration ?? TOAST_DURATION;
    timeoutRef.current = setTimeout(() => {
      setToast((current) => (current?.id === id ? null : current));
      timeoutRef.current = null;
    }, duration);
  }, []);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      <View style={{ flex: 1 }}>
        {children}
        <ToastViewport
          toast={toast}
          onDismiss={dismissToast}
          backgroundSource={backgroundSource}
        />
      </View>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
